import { combineReducers } from 'redux';
import { handleActions } from 'redux-actions';
import fp from 'lodash/fp';

import { utcDateFormatter } from 'common/services/date';
import actions from '../actions';

const extractAttributes = (response, type) => {
  const entities = response?.[type];
  if (!entities) return null;
  const firstEntity = fp.values(entities)[0];
  return firstEntity?.attributes || null;
};

const {
  REWARDS_REQUEST,
  REWARDS_FAILURE,
  REWARDS_SUCCESS,
  REWARD_DETAILS_REQUEST,
  REWARD_DETAILS_FAILURE,
  REWARD_DETAILS_SUCCESS,
} = actions;

const defaults = {
  loading: false,
  error: null,
};

const formatCurrency = (amount, currency) => {
  const numAmount = parseFloat(amount);
  return `${numAmount.toFixed(2)} ${currency}`;
};

const formatPercentage = (decimal) => {
  const percentage = parseFloat(decimal) * 100;
  return `${
    percentage % 1 === 0 ? percentage.toFixed(0) : percentage.toFixed(1)
  }%`;
};

const formatXmn = (amount) => {
  const numAmount = parseFloat(amount);
  return `${numAmount.toFixed(6)} XMN`;
};

const calculateProgress = (startDate, unlockingDate) => {
  const start = new Date(startDate);
  const unlock = new Date(unlockingDate);
  const now = new Date();
  const totalDuration = unlock.getTime() - start.getTime();
  const elapsed = now.getTime() - start.getTime();
  return Math.max(0, Math.min(1, elapsed / totalDuration));
};

const calculateDaysLeft = (unlockingDate) => {
  const unlock = new Date(unlockingDate);
  const now = new Date();
  const diffTime = unlock.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return Math.max(0, diffDays);
};

const formatDate = (dateString) => {
  const displayUtcDate = utcDateFormatter(dateString);
  return displayUtcDate('dd/MM/yyyy');
};

const formatPeriod = (startDate) => {
  const date = new Date(startDate);
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  return `${monthNames[date.getMonth()]} ${date.getFullYear()} rewards`;
};

const transformRewardsData = (payload) => {
  if (!payload) return null;

  const data = extractAttributes(payload, 'rewards');
  if (!data) return null;

  const settlementCurrency = data.settlementCurrency || '';

  const balance = {
    totalSettlement: formatCurrency(
      data.totalXmnBalanceSettlement || '0',
      settlementCurrency
    ),
    totalXmn: formatXmn(data.totalXmnBalance || '0'),
    locked: {
      xmn: formatXmn(data.totalLockedXmn || '0'),
      settlement: formatCurrency(
        data.totalLockedXmnSettlement || '0',
        settlementCurrency
      ),
    },
    unlocked: {
      xmn: formatXmn(data.totalUnlockedXmn || '0'),
      settlement: formatCurrency(
        data.totalUnlockedXmnSettlement || '0',
        settlementCurrency
      ),
    },
  };

  const nextRewardRate = {
    rate: formatPercentage(data.merchantPercentage || '0'),
    description:
      'A variable percentage of your settlement costs is used to purchase XMN tokens that you can withdraw after a 3 month lock period.',
  };

  const schedule = (data.groupedRewards || []).map((item, index) => {
    const unlocked = item.unlocked || false;
    const { unlockingDate } = item;
    const { startDate } = item;
    const progress = calculateProgress(startDate, unlockingDate);
    const daysLeft = calculateDaysLeft(unlockingDate);

    return {
      id: `${index}-${unlockingDate}`,
      amount: formatXmn(item.totalXmnAmount || '0'),
      period: formatPeriod(startDate),
      daysLeft,
      status: unlocked ? 'unlocked' : 'locked',
      startDate: formatDate(startDate),
      unlockDate: formatDate(unlockingDate),
      progress,
      unlockingDate,
      startDateISO: startDate,
    };
  });

  return {
    balance,
    nextRewardRate,
    schedule,
  };
};

const transformRewardDetails = (payload) => {
  if (!payload) return null;

  const data = extractAttributes(payload, 'rewardDetails');
  if (!data) return null;

  const rewards = data.rewards || [];

  return {
    unlockingDate: data.unlockingDate,
    startDate: data.startDate,
    rewards: rewards.map((reward, index) => {
      const displayUtcDate = utcDateFormatter(reward.settlementDate);
      const formattedDate = displayUtcDate('dd MMM, yyyy');
      const rewardCurrency = reward.settlementCurrency || '';

      return {
        id: `${index}-${reward.settlementDate}`,
        settlementDate: formattedDate,
        rate: formatPercentage(reward.rewardPercentage || '0'),
        settlementFee: formatCurrency(
          reward.settlementFee || '0',
          rewardCurrency
        ),
        earnedSettlement: formatCurrency(
          reward.rewardAmount || '0',
          rewardCurrency
        ),
        earnedXmn: formatXmn(reward.xmnAmount || '0'),
      };
    }),
  };
};

const rewardsReducer = handleActions(
  {
    [REWARDS_REQUEST]: () => ({
      ...defaults,
      loading: true,
    }),
    [REWARDS_SUCCESS]: (state, action) => {
      const transformed = transformRewardsData(action.payload);
      return {
        ...defaults,
        data: transformed,
      };
    },
    [REWARDS_FAILURE]: (state, action) => ({
      ...defaults,
      error: action.payload,
    }),
  },
  defaults
);

const rewardDetailsReducer = handleActions(
  {
    [REWARD_DETAILS_REQUEST]: () => ({
      ...defaults,
      loading: true,
    }),
    [REWARD_DETAILS_SUCCESS]: (state, action) => {
      const transformed = transformRewardDetails(action.payload);
      return {
        ...defaults,
        data: transformed,
      };
    },
    [REWARD_DETAILS_FAILURE]: (state, action) => ({
      ...defaults,
      error: action.payload,
    }),
  },
  defaults
);

export default combineReducers({
  overview: rewardsReducer,
  details: rewardDetailsReducer,
});

export const rewardsData = (state) => state.rewards.overview.data;
export const isFetchingRewards = (state) => state.rewards.overview.loading;
export const rewardsError = (state) => state.rewards.overview.error;
export const rewardDetails = (state) => state.rewards.details.data;
export const isFetchingRewardDetails = (state) => state.rewards.details.loading;
export const rewardDetailsError = (state) => state.rewards.details.error;
